import { z } from 'zod';

// Analysis schemas
const ShotDataSchema = z.object({
  id: z.string(),
  playerId: z.string(),
  sessionId: z.string(),
  timestamp: z.string(),
  shotType: z.enum(['drive', 'cut', 'pull', 'sweep', 'defensive', 'loft']),
  ballSpeed: z.number(),
  ballTrajectory: z.object({
    angle: z.number(),
    spin: z.number(),
    direction: z.number()
  }),
  batSpeed: z.number(),
  impactLocation: z.object({
    x: z.number(),
    y: z.number()
  }),
  batAngle: z.number(),
  distance: z.number(),
  height: z.number(),
  boundaryCleared: z.boolean(),
  runs: z.number(),
  timing: z.number(),
  power: z.number(),
  control: z.number(),
  footwork: z.number()
});

const AnalysisResponseSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  shotId: z.string(),
  overallQuality: z.number(),
  qualityBreakdown: z.object({
    technique: z.number(),
    power: z.number(),
    timing: z.number(),
    placement: z.number()
  }),
  techniqueFeedback: z.object({
    strengths: z.array(z.string()),
    improvements: z.array(z.string()),
    keyFocus: z.string()
  }),
  coachingSuggestions: z.array(z.object({
    priority: z.enum(['high', 'medium', 'low']),
    category: z.enum(['technique', 'power', 'timing', 'footwork', 'mental']),
    suggestion: z.string(),
    drills: z.array(z.string()).optional()
  })),
  patterns: z.object({
    identified: z.array(z.string()),
    trend: z.enum(['improving', 'stable', 'declining']).optional(),
    consistency: z.number().optional()
  }).optional(),
  confidence: z.number(),
  processingTime: z.number(),
  model: z.string(),
  provider: z.enum(['ollama', 'openai', 'anthropic', 'mock'])
});

export type ShotData = z.infer<typeof ShotDataSchema>;
export type AnalysisResponse = z.infer<typeof AnalysisResponseSchema>;

class LLMService {
  private baseUrl: string;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();

  constructor() {
    this.baseUrl = import.meta.env.VITE_LLM_SERVICE_URL || 'http://localhost:3001';
    this.connectWebSocket();
  }

  /**
   * Connect to LLM service WebSocket
   */
  private connectWebSocket() {
    try {
      const wsUrl = this.baseUrl.replace('http', 'ws') + '/ws/analysis';
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Connected to LLM service');
        this.reconnectAttempts = 0;
        this.emit('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };

      this.ws.onclose = () => {
        console.log('Disconnected from LLM service');
        this.emit('disconnected');
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to connect to LLM service:', error);
      this.attemptReconnect();
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: { type: string; payload: { intervention?: string } & Record<string, unknown> }) {
    switch (data.type) {
      case 'analysis_result':
        this.emit('analysis', data.payload);
        break;
      case 'intervention_result':
        this.emit('intervention', data.payload.intervention);
        break;
      case 'error':
        this.emit('error', data.payload);
        break;
      default:
        console.warn('Unknown message type:', data.type);
    }
  }

  /**
   * Attempt to reconnect to WebSocket
   */
  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      this.connectWebSocket();
    }, delay);
  }

  /**
   * Send message via WebSocket
   */
  private sendMessage(message: { type: string; payload: unknown }) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
      throw new Error('LLM service is not connected');
    }
  }

  /**
   * Analyze a shot using the LLM service
   */
  async analyzeShot(shotData: ShotData, context?: Record<string, unknown>): Promise<AnalysisResponse> {
    const response = await fetch(`${this.baseUrl}/analyze/shot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shotData,
        context,
        analysisType: 'quick'
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to analyze shot: ${response.statusText}`);
    }

    const data = await response.json();
    return AnalysisResponseSchema.parse(data);
  }

  /**
   * Analyze shot via WebSocket for real-time response
   */
  analyzeShotRealtime(shotData: ShotData, context?: Record<string, unknown>) {
    this.sendMessage({
      type: 'analyze_shot',
      payload: {
        shotData,
        context,
        analysisType: 'quick'
      }
    });
  }

  /**
   * Generate coaching intervention
   */
  async generateIntervention(
    shotData: ShotData,
    previousShots: ShotData[] = [],
    urgency: 'low' | 'medium' | 'high' = 'medium'
  ): Promise<string> {
    const response = await fetch(`${this.baseUrl}/intervention`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shotData,
        previousShots,
        urgency
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to generate intervention: ${response.statusText}`);
    }

    const data = await response.json();
    return data.intervention;
  }

  /**
   * Generate intervention via WebSocket
   */
  generateInterventionRealtime(
    shotData: ShotData,
    previousShots: ShotData[] = [],
    urgency: 'low' | 'medium' | 'high' = 'medium'
  ) {
    this.sendMessage({
      type: 'intervention',
      payload: {
        shotData,
        previousShots,
        urgency
      }
    });
  }

  /**
   * Analyze pattern across multiple shots
   */
  async analyzePattern(shots: ShotData[]): Promise<{ patterns: string[]; trend: string; recommendations: string[] }> {
    const response = await fetch(`${this.baseUrl}/analyze/pattern`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ shots })
    });

    if (!response.ok) {
      throw new Error(`Failed to analyze pattern: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Batch analyze multiple shots
   */
  async analyzeBatch(shots: ShotData[]): Promise<AnalysisResponse[]> {
    const response = await fetch(`${this.baseUrl}/analyze/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ shots })
    });

    if (!response.ok) {
      throw new Error(`Failed to analyze batch: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results;
  }

  /**
   * Check service health
   */
  async checkHealth(): Promise<{ status: string; version?: string; uptime?: number }> {
    const response = await fetch(`${this.baseUrl}/health`);
    if (!response.ok) {
      throw new Error('LLM service is unhealthy');
    }
    return response.json();
  }

  /**
   * Get service metrics
   */
  async getMetrics(): Promise<{ requestsPerMinute: number; averageResponseTime: number; activeModels: string[] }> {
    const response = await fetch(`${this.baseUrl}/metrics`);
    if (!response.ok) {
      throw new Error('Failed to get metrics');
    }
    return response.json();
  }

  /**
   * Event emitter methods
   */
  on(event: string, callback: (...args: unknown[]) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (...args: unknown[]) => void) {
    this.listeners.get(event)?.delete(callback);
  }

  private emit(event: string, ...args: unknown[]) {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  /**
   * Get connection status
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Disconnect from service
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

// Export singleton instance
export const llmService = new LLMService();